import { useState, useEffect, useCallback, useMemo } from 'react';
import { api } from '../contexts/AuthContext';
import { formatPHP } from '../lib/utils';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { ScrollArea } from './ui/scroll-area';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { RefreshCw, Search, GitBranch, History, ShieldCheck, TrendingUp, Boxes } from 'lucide-react';
import { toast } from 'sonner';
import AuthDialog from './AuthDialog';

/**
 * SmartPropagationTool
 * Standalone, anytime cross-branch capital propagation. Scans the latest PO /
 * branch-transfer per product across ALL branches, proposes the chronologically
 * latest acquisition as the "Main Propagator" base, and lets an admin push that
 * (or a per-branch manual override) to chosen branches in one matrix. Reversible
 * via the Propagation History tab (batch undo). Reuses /branch-prices/* engine.
 */
export default function SmartPropagationTool() {
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [products, setProducts] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(false);

  // selection state: { [productId]: { base: string, targets: { [branchId]: { include, override } } } }
  const [sel, setSel] = useState({});

  // review + auth
  const [reviewProduct, setReviewProduct] = useState(null);
  const [authOpen, setAuthOpen] = useState(false);
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim()), 350);
    return () => clearTimeout(t);
  }, [search]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/branch-prices/capital-scan', {
        params: { search: debounced, limit: 60 },
      });
      setProducts(res.data.products || []);
      setBranches(res.data.branches || []);
    } catch {
      toast.error('Failed to load capital scan');
    }
    setLoading(false);
  }, [debounced]);

  useEffect(() => { load(); }, [load]);

  const baseFor = useCallback((p) => {
    const s = sel[p.product_id];
    if (s && s.base !== '' && s.base != null && Number(s.base) > 0) return Number(s.base);
    return Number(p.main_propagator?.price || 0);
  }, [sel]);

  const setBase = (pid, val) => {
    setSel(prev => ({ ...prev, [pid]: { ...(prev[pid] || { targets: {} }), base: val } }));
  };

  const toggleBranch = (pid, bid) => {
    setSel(prev => {
      const cur = prev[pid] || { base: '', targets: {} };
      const t = cur.targets[bid] || { include: false, override: '' };
      return { ...prev, [pid]: { ...cur, targets: { ...cur.targets, [bid]: { ...t, include: !t.include } } } };
    });
  };

  const setOverride = (pid, bid, val) => {
    setSel(prev => {
      const cur = prev[pid] || { base: '', targets: {} };
      const t = cur.targets[bid] || { include: false, override: '' };
      return { ...prev, [pid]: { ...cur, targets: { ...cur.targets, [bid]: { include: true, override: val } } } };
    });
  };

  const selectAll = (p) => {
    setSel(prev => {
      const cur = prev[p.product_id] || { base: '', targets: {} };
      const allOn = branches.every(b => cur.targets[b.id]?.include);
      const next = {};
      branches.forEach(b => { next[b.id] = { include: !allOn, override: cur.targets[b.id]?.override || '' }; });
      return { ...prev, [p.product_id]: { ...cur, targets: next } };
    });
  };

  const selectedTargets = useCallback((p) => {
    const cur = sel[p.product_id];
    if (!cur) return [];
    return branches
      .filter(b => cur.targets[b.id]?.include)
      .map(b => {
        const ov = cur.targets[b.id]?.override;
        const overrideNum = ov !== '' && ov != null ? Number(ov) : null;
        const newCap = overrideNum && overrideNum > 0 ? overrideNum : baseFor(p);
        return { branch: b, override: overrideNum, newCap };
      });
  }, [sel, branches, baseFor]);

  const openReview = (p) => {
    const targets = selectedTargets(p);
    if (targets.length === 0) { toast.warning('Select at least one branch to propagate to'); return; }
    if (baseFor(p) <= 0 && !targets.some(t => t.override > 0)) {
      toast.warning('No base price found — set a manual base or a per-branch price'); return;
    }
    setReviewProduct(p);
  };

  const doPropagate = async () => {
    const p = reviewProduct;
    if (!p) return;
    setApplying(true);
    try {
      const targets = selectedTargets(p).map(t => ({
        branch_id: t.branch.id,
        ...(t.override && t.override > 0 ? { override_capital: t.override } : {}),
      }));
      const res = await api.post('/branch-prices/propagate-capital', {
        product_id: p.product_id,
        base_capital: baseFor(p),
        source_branch_id: p.main_propagator?.branch_id || '',
        source_po_number: p.main_propagator?.po_number || '',
        targets,
      });
      toast.success(res.data.summary || 'Capital propagated');
      setReviewProduct(null);
      setSel(prev => { const n = { ...prev }; delete n[p.product_id]; return n; });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Propagation failed');
    }
    setApplying(false);
  };

  const reviewTargets = useMemo(() => reviewProduct ? selectedTargets(reviewProduct) : [], [reviewProduct, selectedTargets]);

  return (
    <div className="space-y-3 mt-4" data-testid="smart-propagation-tool">
      <div className="rounded-lg border bg-emerald-50/40 p-3 text-[12px] text-emerald-900" style={{ borderColor: 'var(--border-default)' }}>
        <div className="flex items-start gap-2">
          <Boxes size={15} className="text-[#1A4D2E] mt-0.5 shrink-0" />
          <p>
            Scans the <strong>latest PO / branch transfer</strong> per product across every branch. The most
            recent acquisition (by date) is proposed as the <strong>Main Propagator</strong> base.
            Tick a branch to apply that capital (or type a per-branch price), review, then confirm with your Admin PIN.
            A <strong>manual price re-bases the moving average</strong>; the next PO/transfer at that branch folds in per your costing method. Reverse any set from the <strong>Propagation History</strong> tab.
          </p>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <div className="relative flex-1 max-w-sm">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search product name / SKU…"
            className="h-9 pl-8 text-sm" data-testid="propagate-search" />
        </div>
        <Button size="sm" variant="ghost" className="h-9" onClick={load} data-testid="propagate-refresh-btn">
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
        </Button>
        <Badge variant="outline" className="text-xs">{branches.length} branches</Badge>
      </div>

      <ScrollArea className="rounded-lg border" style={{ borderColor: 'var(--border-default)' }}>
        <div className="min-w-max">
          {loading ? (
            <div className="flex items-center justify-center py-16 text-slate-400 text-sm">
              <RefreshCw size={15} className="animate-spin mr-2" /> Scanning capital…
            </div>
          ) : products.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-slate-400 text-sm gap-2">
              <History size={28} className="opacity-40" /> No products found.
            </div>
          ) : (
            <table className="text-sm border-collapse w-full">
              <thead>
                <tr className="bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
                  <th className="text-left px-3 py-2 sticky left-0 bg-slate-50 z-10 min-w-[200px]">Product</th>
                  <th className="text-left px-3 py-2 min-w-[150px]">Latest PO (base)</th>
                  <th className="text-left px-3 py-2 min-w-[110px]">Manual base</th>
                  {branches.map(b => (
                    <th key={b.id} className="text-center px-2 py-2 min-w-[120px] border-l" style={{ borderColor: 'var(--border-default)' }}>
                      {b.name}
                    </th>
                  ))}
                  <th className="text-center px-3 py-2 min-w-[110px] sticky right-0 bg-slate-50 z-10">Action</th>
                </tr>
              </thead>
              <tbody>
                {products.map(p => {
                  const cur = sel[p.product_id] || { base: '', targets: {} };
                  const mp = p.main_propagator;
                  const base = baseFor(p);
                  const nSel = branches.filter(b => cur.targets[b.id]?.include).length;
                  return (
                    <tr key={p.product_id} className="border-t hover:bg-slate-50/40" style={{ borderColor: 'var(--border-default)' }}
                      data-testid={`propagate-row-${p.product_id}`}>
                      {/* Product */}
                      <td className="px-3 py-2 sticky left-0 bg-white z-10">
                        <div className="font-semibold text-slate-800 truncate max-w-[200px]">{p.name}</div>
                        <div className="text-[11px] text-slate-400">{p.sku} · {p.category}</div>
                      </td>
                      {/* Latest PO base + history popover */}
                      <td className="px-3 py-2">
                        {mp ? (
                          <Popover>
                            <PopoverTrigger asChild>
                              <button className="text-left group" data-testid={`propagate-history-${p.product_id}`}>
                                <div className="font-mono font-bold text-[#1A4D2E] flex items-center gap-1">
                                  {formatPHP(mp.price)}
                                  <History size={11} className="text-slate-400 group-hover:text-[#1A4D2E]" />
                                </div>
                                <div className="text-[10px] text-slate-400 truncate max-w-[140px]">
                                  {mp.po_number || (mp.type === 'transfer_in' ? 'Transfer' : 'PO')} · {mp.date} · {mp.branch_name}
                                </div>
                              </button>
                            </PopoverTrigger>
                            <PopoverContent className="w-72 p-3" align="start">
                              <p className="text-[11px] font-semibold text-slate-600 mb-2 flex items-center gap-1">
                                <History size={12} /> Last {p.history.length} acquisition(s)
                              </p>
                              <div className="space-y-1.5">
                                {p.history.map((h, i) => (
                                  <div key={i} className={`flex items-center justify-between text-[11px] rounded px-2 py-1 ${i === 0 ? 'bg-emerald-50' : 'bg-slate-50'}`}>
                                    <div>
                                      <div className="font-medium text-slate-700">{h.branch_name}</div>
                                      <div className="text-slate-400">{h.po_number || (h.type === 'transfer_in' ? 'Transfer' : 'PO')} · {h.date}</div>
                                    </div>
                                    <div className="flex items-center gap-1.5">
                                      <span className="font-mono font-bold text-slate-700">{formatPHP(h.price)}</span>
                                      {i === 0 && <Badge className="bg-[#1A4D2E] text-white text-[9px] px-1.5">Latest</Badge>}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </PopoverContent>
                          </Popover>
                        ) : (
                          <span className="text-[11px] text-slate-400">No PO history</span>
                        )}
                      </td>
                      {/* Manual base override */}
                      <td className="px-3 py-2">
                        <Input value={cur.base ?? ''} onChange={e => setBase(p.product_id, e.target.value)}
                          placeholder={mp ? formatPHP(mp.price).replace('₱', '') : '0.00'} inputMode="decimal"
                          className="h-8 w-24 text-xs font-mono" data-testid={`propagate-base-${p.product_id}`} />
                      </td>
                      {/* Branch matrix cells */}
                      {branches.map(b => {
                        const br = p.branches[b.id] || {};
                        const t = cur.targets[b.id] || { include: false, override: '' };
                        const ovNum = t.override !== '' && t.override != null ? Number(t.override) : null;
                        const newCap = ovNum && ovNum > 0 ? ovNum : base;
                        const higher = newCap > (br.current_capital || 0);
                        return (
                          <td key={b.id} className={`px-2 py-2 text-center border-l align-top ${t.include ? 'bg-emerald-50/60' : ''}`}
                            style={{ borderColor: 'var(--border-default)' }}>
                            <button onClick={() => toggleBranch(p.product_id, b.id)}
                              className={`w-full rounded-md border px-1.5 py-1 transition-colors ${t.include ? 'border-[#1A4D2E] bg-emerald-50' : 'border-transparent hover:border-slate-200'}`}
                              data-testid={`propagate-cell-${p.product_id}-${b.id}`}>
                              <div className="text-[10px] text-slate-400">cur</div>
                              <div className="text-xs font-mono text-slate-600">{formatPHP(br.current_capital || 0)}</div>
                              <div className="text-[9px] text-slate-300">stk {br.stock_on_hand ?? 0}</div>
                            </button>
                            {t.include && (
                              <>
                                <Input value={t.override ?? ''} onChange={e => setOverride(p.product_id, b.id, e.target.value)}
                                  placeholder={formatPHP(base).replace('₱', '')} inputMode="decimal"
                                  className="h-7 w-20 mx-auto mt-1 text-[11px] font-mono px-1.5 text-center"
                                  data-testid={`propagate-override-${p.product_id}-${b.id}`} />
                                <div className={`text-[10px] font-mono font-bold mt-0.5 flex items-center justify-center gap-0.5 ${higher ? 'text-red-600' : 'text-emerald-600'}`}>
                                  {higher && <TrendingUp size={9} />}→ {formatPHP(newCap)}
                                </div>
                              </>
                            )}
                          </td>
                        );
                      })}
                      {/* Action */}
                      <td className="px-3 py-2 text-center sticky right-0 bg-white z-10">
                        <Button size="sm" variant="ghost" className="h-7 text-[10px] text-slate-500 w-full mb-1"
                          onClick={() => selectAll(p)} data-testid={`propagate-selectall-${p.product_id}`}>
                          <GitBranch size={11} className="mr-1" /> All
                        </Button>
                        <Button size="sm" className="h-7 text-[11px] bg-[#1A4D2E] hover:bg-[#14532d] text-white w-full"
                          disabled={nSel === 0}
                          onClick={() => openReview(p)} data-testid={`propagate-apply-${p.product_id}`}>
                          Apply ({nSel})
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </ScrollArea>

      {/* Review dialog */}
      <Dialog open={!!reviewProduct} onOpenChange={(o) => !o && setReviewProduct(null)}>
        <DialogContent className="max-w-md" data-testid="propagate-review-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#1A4D2E]" style={{ fontFamily: 'Manrope' }}>
              <ShieldCheck size={18} /> Review propagation
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              <span className="font-semibold text-slate-700">{reviewProduct?.name}</span> — applying capital to{' '}
              {reviewTargets.length} branch(es). Base{' '}
              <span className="font-mono font-bold text-[#1A4D2E]">{reviewProduct ? formatPHP(baseFor(reviewProduct)) : ''}</span>
              {reviewProduct?.main_propagator?.po_number ? <> from <span className="font-mono">{reviewProduct.main_propagator.po_number}</span></> : null}.
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-72 overflow-auto divide-y rounded-lg border" style={{ borderColor: 'var(--border-default)' }}>
            {reviewTargets.map(t => (
              <div key={t.branch.id} className="flex items-center justify-between px-3 py-2" data-testid={`review-row-${t.branch.id}`}>
                <div>
                  <div className="text-sm font-medium text-slate-800">{t.branch.name}</div>
                  <div className="text-[11px] text-slate-400">
                    {formatPHP((reviewProduct?.branches?.[t.branch.id]?.current_capital) || 0)} → {t.override ? 'manual' : 'base'}
                  </div>
                </div>
                <div className="font-mono font-bold text-[#1A4D2E]">{formatPHP(t.newCap)}</div>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setReviewProduct(null)} data-testid="review-cancel-btn">Cancel</Button>
            <Button className="bg-[#1A4D2E] hover:bg-[#14532d] text-white" disabled={applying}
              onClick={() => setAuthOpen(true)} data-testid="review-confirm-btn">
              <ShieldCheck size={14} className="mr-1.5" /> Confirm with PIN
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AuthDialog
        open={authOpen}
        onClose={() => setAuthOpen(false)}
        mode="either"
        context="cross_branch_capital_propagation"
        onVerified={() => { setAuthOpen(false); doPropagate(); }}
      />
    </div>
  );
}
